/**
 * Identidade no cliente: guarda do token, `Authorization` em toda chamada e
 * tratamento de 401.
 *
 * Três decisões que valem mais que o código:
 *
 * 1. **O token vive em memória e é espelhado no `sessionStorage`, nunca no
 *    `localStorage`.** O `sessionStorage` morre com a aba, então uma credencial
 *    vazada por XSS tem validade de uma aba aberta em vez de sobreviver a
 *    reinicializações do navegador. Não é defesa contra XSS — nada guardado em
 *    JS é —, é redução de janela. A defesa de verdade é o segredo de renovação
 *    ficar num cookie `httpOnly`, fora do alcance de qualquer script.
 *
 * 2. **O `principal` NÃO é persistido.** Só o token (assinado, opaco para nós)
 *    atravessa recarregamentos; quem diz tenant, workspace, usuário e papéis é
 *    sempre o servidor, em `GET {api}/auth/me`. Se guardássemos o principal no
 *    armazenamento local, o tenant exibido passaria a ser um dado editável pelo
 *    cliente — exatamente o que a v0.2 existe para acabar. O cliente nunca
 *    escolhe tenant, e por isso nenhuma requisição daqui envia `tenant_id`.
 *
 * 3. **401 tem duas causas e elas não podem ser confundidas.** A API do
 *    Presence responde 401 tanto para token de identidade inválido quanto para
 *    ticket de WebSocket recusado (`code: "ticket_*"`, ver `http/api.ts`).
 *    Derrubar a sessão inteira por um ticket vencido seria trocar um problema
 *    pequeno e recuperável por logout do usuário. Por isso o código do erro é
 *    lido antes de qualquer reação.
 *
 * O formato exato das respostas ainda está sendo escrito na outra frente. Como
 * em `panel/health.ts`, este módulo não confia no formato: aceita `unknown` e
 * extrai o que reconhece, tolerando snake_case e camelCase. O que não
 * reconhecer vira `null` — e o portão de entrada mostra isso com todas as
 * letras em vez de inventar uma identidade.
 */

import { U1_API_PATH } from '../client/config';

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

/** Uma entidade nomeada resolvida pelo servidor (tenant ou workspace). */
export interface IdentidadeNomeada {
  readonly id: string;
  /** apelido curto e estável, quando o servidor expõe um */
  readonly slug: string | null;
  /** nome de exibição; `null` quando o servidor não mandou */
  readonly name: string | null;
}

export interface UsuarioPrincipal {
  readonly id: string;
  readonly email: string | null;
  readonly name: string | null;
}

/**
 * Quem o servidor decidiu que somos.
 *
 * Este objeto é leitura, nunca pedido: nenhum campo daqui volta para o servidor
 * como parâmetro. `workspace` é anulável porque um principal pode existir antes
 * de ter workspace escolhido; `tenant` e `user` não são — sem eles não há
 * sessão, e uma sessão sem tenant é justamente o buraco que o `DEV_TENANT`
 * deixava aberto.
 */
export interface Principal {
  readonly tenant: IdentidadeNomeada;
  readonly workspace: IdentidadeNomeada | null;
  readonly user: UsuarioPrincipal;
  /** papéis já resolvidos pelo servidor para este tenant/workspace */
  readonly roles: readonly string[];
}

export type AuthPhase =
  /** ainda tentando recuperar a sessão da aba — não sabemos se há login */
  | 'restaurando'
  /** não há sessão: o portão mostra a tela de entrada */
  | 'anonimo'
  /** há principal resolvido pelo servidor */
  | 'autenticado';

export interface AuthState {
  readonly fase: AuthPhase;
  readonly principal: Principal | null;
  /** última falha em linguagem de gente; `null` quando não há o que contar */
  readonly error: string | null;
  /** há requisição de identidade em voo (entrada, saída ou renovação) */
  readonly ocupado: boolean;
}

/** Credenciais aceitas. O cliente informa quem é, jamais onde quer entrar. */
export type PedidoDeLogin =
  /** enquanto não existe provedor real (exige `DEV_AUTH_BYPASS` no servidor) */
  | { readonly modo: 'dev'; readonly email: string }
  /** já previsto para quando houver provedor: mesma rota de resposta */
  | { readonly modo: 'senha'; readonly email: string; readonly senha: string };

export class AuthError extends Error {
  constructor(
    message: string,
    /** status HTTP, ou `null` quando a falha foi de rede */
    readonly status: number | null,
    /** código de máquina devolvido pela API, quando houver */
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

export type Ouvinte = (estado: AuthState) => void;
export type Cancelar = () => void;

// ---------------------------------------------------------------------------
// Endereços
// ---------------------------------------------------------------------------

/**
 * As rotas de identidade, derivadas do mesmo `U1_API_PATH` do resto do cliente
 * (ADR-008: um domínio só, API em rota). Ficam exportadas para a frente do
 * servidor ter um alvo escrito em vez de um combinado verbal.
 */
export const AUTH_PATHS = {
  /** GET  — resolve o principal do token apresentado */
  me: `${U1_API_PATH}/auth/me`,
  /** POST — login de desenvolvimento; some quando houver provedor real */
  devLogin: `${U1_API_PATH}/auth/dev-login`,
  /** POST — login por credencial (ainda não implementado no servidor) */
  login: `${U1_API_PATH}/auth/login`,
  /** POST — troca o cookie httpOnly de renovação por um token novo */
  refresh: `${U1_API_PATH}/auth/refresh`,
  /** POST — invalida a sessão do lado do servidor */
  logout: `${U1_API_PATH}/auth/logout`,
} as const;

const CHAVE_ARMAZENAMENTO = 'will.u1.identidade';

/**
 * Margem para renovar antes da hora. 60 s cobre relógio dessincronizado entre
 * navegador e servidor e ainda dá tempo de a renovação ir e voltar antes de
 * qualquer chamada esbarrar num token vencido.
 */
const MARGEM_RENOVACAO_MS = 60_000;

// ---------------------------------------------------------------------------
// Estado do módulo
// ---------------------------------------------------------------------------

let tokenAtual: string | null = null;
let estado: AuthState = { fase: 'restaurando', principal: null, error: null, ocupado: false };

const ouvintes = new Set<Ouvinte>();

/** Renovação e restauração são únicas em voo: duas abas do StrictMode, um pedido. */
let renovacaoEmVoo: Promise<boolean> | null = null;
let restauracaoEmVoo: Promise<AuthState> | null = null;
let timerRenovacao: ReturnType<typeof setTimeout> | null = null;

// ---------------------------------------------------------------------------
// Assinatura
// ---------------------------------------------------------------------------

export function estadoDaSessao(): AuthState {
  return estado;
}

export function assinarSessao(ouvinte: Ouvinte): Cancelar {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

function publicar(mudanca: Partial<AuthState>): void {
  estado = { ...estado, ...mudanca };
  for (const ouvinte of ouvintes) ouvinte(estado);
}

// ---------------------------------------------------------------------------
// Guarda do token
// ---------------------------------------------------------------------------

function lerTokenGuardado(): string | null {
  try {
    const cru = window.sessionStorage.getItem(CHAVE_ARMAZENAMENTO);
    return cru !== null && cru !== '' ? cru : null;
  } catch {
    // sessionStorage bloqueado (modo privado, iframe sem permissão). Perder a
    // retomada custa um login a mais; quebrar o boot custaria o app inteiro.
    return null;
  }
}

function guardarToken(token: string | null): void {
  tokenAtual = token;
  agendarRenovacao(token);
  try {
    if (token === null) window.sessionStorage.removeItem(CHAVE_ARMAZENAMENTO);
    else window.sessionStorage.setItem(CHAVE_ARMAZENAMENTO, token);
  } catch {
    // ver lerTokenGuardado(): armazenamento indisponível não é erro fatal —
    // a sessão segue viva em memória até a aba fechar.
  }
}

/**
 * Decodifica o `exp` do JWT — e só ele, e só para saber **quando** renovar.
 *
 * Nada do que sai daqui é tratado como autoridade: o payload não é verificado
 * neste lado e não pode ser. Quem decide o que o token permite é o servidor;
 * aqui ele serve de despertador. Um `exp` mentiroso adianta ou atrasa uma
 * renovação, no pior caso.
 */
export function expiraEm(token: string): number | null {
  const partes = token.split('.');
  if (partes.length !== 3) return null;

  const carga = partes[1];
  if (typeof carga !== 'string' || carga === '') return null;

  try {
    const json = decodificarBase64Url(carga);
    const dado: unknown = JSON.parse(json);
    if (typeof dado !== 'object' || dado === null) return null;
    const exp = (dado as Record<string, unknown>)['exp'];
    // `exp` é epoch em segundos (RFC 7519), não em milissegundos.
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function decodificarBase64Url(valor: string): string {
  const base64 = valor.replace(/-/g, '+').replace(/_/g, '/');
  const preenchido = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const bruto = window.atob(preenchido);
  // `atob` devolve bytes latin-1; o payload é UTF-8. Sem esta volta, um nome
  // com acento (que aqui é a regra, não a exceção) chega corrompido.
  const bytes = Uint8Array.from(bruto, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Renovar antes de o token vencer é o que evita o 401 no meio de uma ação do
 * usuário. O 401 continua tratado — relógio errado, servidor reiniciado, aba
 * dormindo —, mas deixa de ser o caminho normal.
 */
function agendarRenovacao(token: string | null): void {
  if (timerRenovacao !== null) {
    clearTimeout(timerRenovacao);
    timerRenovacao = null;
  }
  if (token === null) return;

  const vence = expiraEm(token);
  if (vence === null) return; // sem validade legível, resta reagir ao 401

  const emQuanto = vence - Date.now() - MARGEM_RENOVACAO_MS;
  timerRenovacao = setTimeout(
    () => {
      void renovar();
    },
    Math.max(emQuanto, 1_000),
  );
}

// ---------------------------------------------------------------------------
// Leitura tolerante do principal
// ---------------------------------------------------------------------------

const ehRegistro = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Primeira chave presente vence; as variantes existem porque o formato ainda não fechou. */
function escolher(fonte: Record<string, unknown>, chaves: readonly string[]): unknown {
  for (const chave of chaves) {
    const valor = fonte[chave];
    if (valor !== undefined && valor !== null) return valor;
  }
  return undefined;
}

function comoTexto(v: unknown): string | null {
  if (typeof v === 'string' && v.trim() !== '') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

/**
 * Lê tenant/workspace em três formatos: objeto aninhado (`{tenant:{id}}`),
 * identificador solto (`{tenant:"acme"}`) e campo achatado (`{tenant_id:…}`).
 */
function lerEntidade(
  raiz: Record<string, unknown>,
  nome: 'tenant' | 'workspace',
): IdentidadeNomeada | null {
  const aninhado = raiz[nome];

  if (ehRegistro(aninhado)) {
    const id = comoTexto(escolher(aninhado, ['id', `${nome}_id`, 'uuid', 'key']));
    if (id === null) return null;
    return {
      id,
      slug: comoTexto(escolher(aninhado, ['slug', 'handle', 'code'])),
      name: comoTexto(escolher(aninhado, ['name', 'title', 'label', 'display_name', 'displayName'])),
    };
  }

  const id = comoTexto(aninhado) ?? comoTexto(escolher(raiz, [`${nome}_id`, `${nome}Id`]));
  if (id === null) return null;

  return {
    id,
    slug: comoTexto(escolher(raiz, [`${nome}_slug`, `${nome}Slug`])),
    name: comoTexto(escolher(raiz, [`${nome}_name`, `${nome}Name`])),
  };
}

function lerUsuario(raiz: Record<string, unknown>): UsuarioPrincipal | null {
  const aninhado = escolher(raiz, ['user', 'usuario', 'subject']);

  if (ehRegistro(aninhado)) {
    const id = comoTexto(escolher(aninhado, ['id', 'user_id', 'sub', 'uuid']));
    if (id === null) return null;
    return {
      id,
      email: comoTexto(escolher(aninhado, ['email', 'mail'])),
      name: comoTexto(escolher(aninhado, ['name', 'display_name', 'displayName', 'full_name'])),
    };
  }

  const id = comoTexto(aninhado) ?? comoTexto(escolher(raiz, ['user_id', 'userId', 'sub']));
  if (id === null) return null;

  return {
    id,
    email: comoTexto(escolher(raiz, ['email', 'user_email'])),
    name: comoTexto(escolher(raiz, ['name', 'user_name', 'display_name'])),
  };
}

function lerPapeis(raiz: Record<string, unknown>): readonly string[] {
  const cru = escolher(raiz, ['roles', 'papeis', 'scopes', 'permissions']);

  // Uma string com papéis separados por espaço é o formato de `scope` do OAuth,
  // e é plausível que a outra frente o use. Aceitar os dois custa uma linha.
  if (typeof cru === 'string') return cru.split(/[\s,]+/).filter((p) => p !== '');
  if (Array.isArray(cru)) return cru.map(comoTexto).filter((p): p is string => p !== null);
  return [];
}

/**
 * Converte a resposta do servidor em `Principal`, ou `null` quando o essencial
 * não veio. `null` aqui significa "não sei quem é" — e não saber quem é tem uma
 * única resposta honesta: continuar anônimo. Adivinhar tenant seria reintroduzir
 * o `DEV_TENANT` por outro caminho.
 */
export function lerPrincipal(payload: unknown): Principal | null {
  if (!ehRegistro(payload)) return null;

  // o corpo pode vir embrulhado em {principal:…}, {data:…} ou {identity:…}
  const embrulho = escolher(payload, ['principal', 'data', 'identity', 'result']);
  const raiz = ehRegistro(embrulho) ? embrulho : payload;

  const tenant = lerEntidade(raiz, 'tenant');
  const user = lerUsuario(raiz);
  if (tenant === null || user === null) return null;

  return { tenant, workspace: lerEntidade(raiz, 'workspace'), user, roles: lerPapeis(raiz) };
}

/** Extrai o token de acesso da resposta de login/renovação. */
function lerToken(payload: unknown): string | null {
  if (!ehRegistro(payload)) return null;
  const embrulho = escolher(payload, ['data', 'result']);
  const raiz = ehRegistro(embrulho) ? embrulho : payload;
  return comoTexto(escolher(raiz, ['access_token', 'accessToken', 'token', 'jwt', 'id_token']));
}

// ---------------------------------------------------------------------------
// Requisições
// ---------------------------------------------------------------------------

/**
 * O `Authorization` só é anexado a endereços da nossa própria origem.
 *
 * A ADR-008 garante que hoje todos são — mas "hoje todos são" não é invariante,
 * e vazar o token para um terceiro por causa de uma URL absoluta esquecida num
 * refactor é um acidente barato de prevenir e caro de descobrir.
 */
function ehMesmaOrigem(alvo: string): boolean {
  try {
    return new URL(alvo, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

function urlDe(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/** Lê `{error, code}` da resposta sem consumi-la para quem chamou. */
async function lerFalha(res: Response): Promise<{ mensagem: string | null; code: string | null }> {
  try {
    const dado: unknown = await res.clone().json();
    if (!ehRegistro(dado)) return { mensagem: null, code: null };
    return {
      mensagem: comoTexto(escolher(dado, ['error', 'message', 'detail'])),
      code: comoTexto(escolher(dado, ['code', 'error_code'])),
    };
  } catch {
    return { mensagem: null, code: null };
  }
}

/**
 * `fetch` com identidade: anexa o token, e um 401 de identidade dispara uma
 * renovação e uma única repetição.
 *
 * Toda chamada do app à API do U1 deve passar por aqui — é o que garante que
 * nenhuma delas precise (nem consiga) informar em qual tenant está operando: o
 * tenant vai dentro do token, resolvido no servidor.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const alvo = urlDe(input);
  const mesmaOrigem = ehMesmaOrigem(alvo);

  const disparar = async (): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (tokenAtual !== null && mesmaOrigem) {
      headers.set('Authorization', `Bearer ${tokenAtual}`);
    }
    return fetch(input, {
      ...init,
      headers,
      // Mesma origem (ADR-008): o cookie httpOnly de renovação viaja sem
      // configuração extra e sem `SameSite=None`.
      credentials: init.credentials ?? 'same-origin',
    });
  };

  const resposta = await disparar();
  if (resposta.status !== 401 || !mesmaOrigem) return resposta;

  const { code } = await lerFalha(resposta);

  // 401 de ticket não é 401 de identidade (ver `http/api.ts`): o ticket do
  // WebSocket vence em 120 s por projeto, e derrubar o login por causa dele
  // trocaria uma reconexão por um logout.
  if (code !== null && code.startsWith('ticket_')) return resposta;

  const renovou = await renovar();
  if (!renovou) return resposta;

  return disparar();
}

/**
 * Troca o cookie de renovação por um token novo.
 *
 * Devolve `false` — e derruba a sessão — quando o servidor recusa. Falha de
 * rede não derruba: ficar sem internet por dez segundos não é o mesmo que ter a
 * sessão revogada, e deslogar alguém por causa disso é castigo por conectividade.
 */
export function renovar(): Promise<boolean> {
  if (renovacaoEmVoo !== null) return renovacaoEmVoo;

  publicar({ ocupado: true });

  renovacaoEmVoo = (async (): Promise<boolean> => {
    try {
      const res = await fetch(AUTH_PATHS.refresh, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { accept: 'application/json' },
      });

      if (!res.ok) {
        // 4xx aqui é veredito: o cookie não vale mais.
        if (res.status < 500) {
          derrubar('Sua sessão expirou. Entre de novo.');
          return false;
        }
        return false;
      }

      const corpo: unknown = await res.json();
      const token = lerToken(corpo);
      if (token === null) {
        derrubar('O servidor renovou a sessão sem devolver token — contrato divergente.');
        return false;
      }

      guardarToken(token);

      // A renovação pode trazer o principal junto; quando não traz, o que já
      // temos continua valendo — o token novo é do mesmo sujeito.
      const principal = lerPrincipal(corpo) ?? estado.principal;
      publicar({ fase: principal !== null ? 'autenticado' : estado.fase, principal, error: null });
      return true;
    } catch {
      return false; // rede: ver comentário acima, não derruba
    } finally {
      renovacaoEmVoo = null;
      publicar({ ocupado: false });
    }
  })();

  return renovacaoEmVoo;
}

/** Pergunta ao servidor quem somos. É a única fonte do `principal`. */
async function resolverPrincipal(): Promise<Principal | null> {
  const res = await authFetch(AUTH_PATHS.me, { headers: { accept: 'application/json' }, cache: 'no-store' });

  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) {
    const { mensagem } = await lerFalha(res);
    throw new AuthError(mensagem ?? `GET ${AUTH_PATHS.me} respondeu ${res.status}`, res.status);
  }

  const principal = lerPrincipal(await res.json());
  if (principal === null) {
    throw new AuthError(
      `Resposta de ${AUTH_PATHS.me} sem tenant ou usuário — contrato divergente.`,
      res.status,
    );
  }
  return principal;
}

/**
 * Recupera a sessão da aba, se houver.
 *
 * Chamada uma vez no boot. Ela sempre termina em `anonimo` ou `autenticado` —
 * nunca deixa a interface presa em `restaurando`, porque um portão que não
 * decide é um app que não abre.
 */
export function restaurarSessao(): Promise<AuthState> {
  if (restauracaoEmVoo !== null) return restauracaoEmVoo;

  restauracaoEmVoo = (async (): Promise<AuthState> => {
    const guardado = lerTokenGuardado();
    if (guardado !== null) {
      tokenAtual = guardado;
      agendarRenovacao(guardado);
    }

    try {
      let principal = tokenAtual !== null ? await resolverPrincipal() : null;

      // Sem token em memória, ainda pode haver cookie de renovação vivo (outra
      // aba, ou o próprio sessionStorage bloqueado). Tentar custa uma requisição
      // e evita pedir senha a quem já está logado.
      if (principal === null && (await renovar())) {
        principal = estado.principal ?? (await resolverPrincipal());
      }

      if (principal === null) {
        guardarToken(null);
        publicar({ fase: 'anonimo', principal: null, error: null, ocupado: false });
      } else {
        publicar({ fase: 'autenticado', principal, error: null, ocupado: false });
      }
    } catch (causa) {
      guardarToken(null);
      publicar({
        fase: 'anonimo',
        principal: null,
        error: causa instanceof Error ? causa.message : String(causa),
        ocupado: false,
      });
    } finally {
      restauracaoEmVoo = null;
    }

    return estado;
  })();

  return restauracaoEmVoo;
}

/**
 * Entra. O corpo carrega apenas quem a pessoa diz ser — nunca onde ela quer
 * entrar. Tenant, workspace e papéis chegam de volta resolvidos pelo servidor.
 */
export async function entrar(pedido: PedidoDeLogin): Promise<Principal> {
  const rota = pedido.modo === 'dev' ? AUTH_PATHS.devLogin : AUTH_PATHS.login;
  const corpo =
    pedido.modo === 'dev'
      ? { email: pedido.email }
      : { email: pedido.email, password: pedido.senha };

  publicar({ ocupado: true, error: null });

  try {
    const res = await fetch(rota, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(corpo),
    });

    if (!res.ok) {
      const { mensagem, code } = await lerFalha(res);
      throw new AuthError(mensagem ?? explicar(res.status, pedido.modo), res.status, code);
    }

    const payload: unknown = await res.json();
    const token = lerToken(payload);
    if (token === null) {
      throw new AuthError('O servidor aceitou o login mas não devolveu token — contrato divergente.', res.status);
    }
    guardarToken(token);

    // O principal pode vir na resposta do login; quando não vem, perguntamos.
    // Em nenhum dos dois caminhos ele é montado aqui.
    const principal = lerPrincipal(payload) ?? (await resolverPrincipal());
    if (principal === null) {
      throw new AuthError('O servidor não disse a que tenant esta sessão pertence.', res.status);
    }

    publicar({ fase: 'autenticado', principal, error: null, ocupado: false });
    return principal;
  } catch (causa) {
    guardarToken(null);
    const erro =
      causa instanceof AuthError
        ? causa
        : new AuthError(`Não consegui falar com o servidor de identidade: ${String(causa)}`, null);
    publicar({ fase: 'anonimo', principal: null, error: erro.message, ocupado: false });
    throw erro;
  }
}

/**
 * Sai. Avisa o servidor para invalidar o cookie de renovação e limpa o lado de
 * cá **mesmo que o aviso falhe**: uma saída que depende da rede não é uma saída.
 */
export async function sair(): Promise<void> {
  publicar({ ocupado: true });
  try {
    await fetch(AUTH_PATHS.logout, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(tokenAtual !== null ? { Authorization: `Bearer ${tokenAtual}` } : {}),
      },
      // A saída precisa acontecer mesmo se a aba estiver fechando.
      keepalive: true,
    });
  } catch {
    // ver acima: falha de rede não impede a limpeza local
  } finally {
    derrubar(null);
  }
}

/** Limpa tudo do lado do cliente e volta a `anonimo`, com ou sem explicação. */
export function derrubar(motivo: string | null): void {
  guardarToken(null);
  publicar({ fase: 'anonimo', principal: null, error: motivo, ocupado: false });
}

function explicar(status: number, modo: PedidoDeLogin['modo']): string {
  if (status === 404 && modo === 'dev') {
    return 'Este servidor não tem login de desenvolvimento. Ligue DEV_AUTH_BYPASS ou use um provedor real.';
  }
  if (status === 403 && modo === 'dev') {
    return 'O login de desenvolvimento está desligado neste servidor (DEV_AUTH_BYPASS=false).';
  }
  if (status === 401) return 'Credenciais recusadas.';
  if (status === 429) return 'Tentativas demais em pouco tempo. Espere um instante.';
  return `O servidor de identidade respondeu ${status}.`;
}
